/**
 * ErrorBoundary.jsx — catches render/runtime crashes in the subtree so a single
 * broken component shows a readable error instead of black-screening the whole app.
 * The exact message + component stack are displayed (and logged) so bugs are
 * diagnosable in production without DevTools.
 */
import React from 'react';
import { tr } from '@quantum/ui';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught:', error, info?.componentStack);
    this.setState({ info });
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const msg = error?.message || String(error);
    const stack = (info?.componentStack || error?.stack || '').toString();

    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999, overflow: 'auto',
        background: 'radial-gradient(ellipse at top, #161b25 0%, #0b0e14 70%)',
        color: 'var(--hi)', fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 16, padding: 28, textAlign: 'center',
      }}>
        <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, color: 'var(--bear)' }}>{tr('Something crashed')}</div>
        <div style={{
          fontFamily: '"JetBrains Mono", monospace', fontSize: 'var(--fs-base)', color: '#ffd1d6',
          maxWidth: 760, lineHeight: 1.55, background: 'color-mix(in srgb, var(--bear) 8%, transparent)',
          borderRadius: 10, padding: '12px 14px',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>{msg}</div>
        {stack && (
          <pre style={{
            fontFamily: '"JetBrains Mono", monospace', fontSize: 'var(--fs-xs)', color: 'var(--mid)',
            maxWidth: 760, maxHeight: 280, overflow: 'auto', textAlign: 'left',
            background: 'var(--s2)', 
            borderRadius: 10, padding: '12px 14px', width: '100%',
          }}>{stack.trim()}</pre>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => this.setState({ error: null, info: null })} style={{
            padding: '9px 18px', borderRadius: 9, cursor: 'pointer', color: 'var(--acc)',
            background: 'linear-gradient(180deg, color-mix(in srgb, var(--acc) 22%, transparent), color-mix(in srgb, var(--acc) 6%, transparent))',
            fontWeight: 600, fontSize: 'var(--fs-base)',
          }}>{tr('Try again')}</button>
          <button onClick={() => window.location.reload()} style={{
            padding: '9px 18px', borderRadius: 9, cursor: 'pointer', color: 'var(--mid)',
            background: 'color-mix(in srgb, var(--hi) 5%, transparent)', fontSize: 'var(--fs-base)',
          }}>{tr('Reload')}</button>
        </div>
      </div>
    );
  }
}
